import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { Field as VeeField, useForm, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { useStore } from "vuex";
import type { FormSchema, FieldSchema } from "@/models/form";


export default /*@__PURE__*/_defineComponent({
  __name: 'BorrowAmountLOC',
  props: {
    disabled: { type: Boolean, default: false }
  },
  setup(__props: any, { expose: __expose }) {

const store = useStore();

const props = __props;

const amount = ref<string | number | undefined>(undefined);

const showField = ref<boolean>(false);

const programMaximumLoanAmount = computed(() => store.state.appData?.application?.loanAmount);

const programMinimumLoanAmount = computed(
  () => store.getters["PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT"],
);

const programMinimumLoanAmountDisplay = computed(
  () => store.getters["PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT_DISPLAY"],
);

const minimumNotMetError = computed<string>(() => {
  return `Your request for funds must be greater than ${programMinimumLoanAmountDisplay.value}. Please enter an amount greater than or equal to ${programMinimumLoanAmountDisplay.value}.`;
});

const validationSchema = computed(() => {
  const schema = yup.object({
    amount: yup
      .number()
      .transform(value => (isNaN(value) ? undefined : value))
      .required("Amount is required")
      .max(
        programMaximumLoanAmount.value,
        "The amount requested is more than the total line of credit you've been approved for.",
      )
      .test(
        "Amount is valid",
        `${minimumNotMetError.value} You may also enter $0 if you are unsure.`,
        function (value) {
          const { path, createError } = this;
          if (value === null || value === undefined) {
            return true; // Handled by .required()
          }

          if (value < programMinimumLoanAmount.value && value !== 0) {
            return createError({
              path,
              message: `${minimumNotMetError.value} You may also enter $0 if you are unsure.`,
            });
          }
          return true;
        },
      ),
  });
  return showField.value ? schema : yup.object({});
});

const { values, errors, setValues } = useForm({
  validationSchema: validationSchema,
});

const hasErrors = computed(() => Object.keys(errors.value).length > 0);

let schema = ref<FormSchema>({
  amount: {
    name: "amount",
    type: "money",
    subtype: "initialDrawAmount",
    hideLabel: true,
    required: true,
    allowZero: true,
    allowDecimals: false,
    errorMessage: "An amount to borrow is required.",
    errors: [] as string[],
    minimum: programMinimumLoanAmount.value,
    productTypeId: store.state.appData?.application?.productTypeId,
    validate: (schema: FieldSchema) => {
      schema.valid = !hasErrors.value;
      schema.errors = errors.value.amount ? [errors.value.amount] : [];
    },
  },
});

function updateAmount(newAmount: number | string | undefined, shouldValidate = true) {
  amount.value = newAmount;

  if (newAmount === "") {
    amount.value = undefined;
  } else {
    amount.value = newAmount;
  }

  if (isNaN(Number(newAmount))) {
    setValues({ amount: undefined }, shouldValidate);
    return;
  }

  setValues({ amount: Number(amount.value) }, shouldValidate);
}

// Watchers
watch(
  () => amount.value,
  (newValue, oldValue) => {
    if (newValue === oldValue) {
      return;
    }

    updateAmount(newValue);
  },
);

onMounted(() => {
  showField.value = true;
});

onBeforeUnmount(() => {
  showField.value = false;
});

__expose({
	props,
  validationSchema,
  values,
  amount,
  setValues,
  hasErrors,
  updateAmount,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(VeeField), {
    name: "amount",
    modelValue: amount.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((amount).value = $event)),
    modelModifiers: { number: true }
  }, {
    default: _withCtx(() => [
      _createVNode(BaseFormField, {
        modelValue: amount.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((amount).value = $event)),
        schema: _unref(schema).amount,
        compare: {
        maximum: programMaximumLoanAmount.value,
        minimum: programMinimumLoanAmount.value,
        minimumDisplay: programMinimumLoanAmountDisplay.value,
      },
        disabled: __props.disabled
      }, {
        errors: _withCtx(() => [
          _createVNode(_unref(ErrorMessage), {
            class: "error-message",
            name: "amount"
          })
        ]),
        _: 1
      }, 8, ["modelValue", "schema", "compare", "disabled"])
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})